/**
 * The records page's people view (the People menu item): one row per human,
 * with every submission they sent grouped under them.
 *
 * It filters by the same body as the rows view, so the segment and
 * the filters on screen carry over exactly: a person is listed when any of
 * their submissions matches, and their counts and value are over all of
 * them. Its own search, sort and "more than one" narrow the people further.
 *
 * Until stored people are switched on the server groups by email, and the
 * view says so: a person is then exactly an email address, and nothing can
 * be merged or unlinked.
 *
 * With the CRM area on, each person shows who owns them, the list can be
 * narrowed to an owner (mine, nobody's, someone's), and someone with
 * assign_records can tick people and give them to an owner or send them
 * through a rotation. A sales rep's list holds only the people they may see:
 * the server narrows it.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Checkbox, Chip, Group, Loader, Pagination, Select, Table, Text, TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconArrowsShuffle, IconInbox, IconSearch } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useIdentitySettings, usePeopleSearch } from '../../api/people';
import type { LeadFilters } from '../../api/queries';
import { FONTS, TOKENS, formatGBP, formatNumber } from '../../theme';
import { SpeedChip } from '../../ui';
import { relativeTime } from '../../ui/format';
import { useVocab } from '../../vocab';
import { formatDay, useCrmPermissions } from '../crm/crmShared';
import { personName, StreamChips } from './peopleShared';
import { AssignPeopleModal, BulkAssignBar, OwnerFilterSelect } from '../assignment/OwnerControls';
import { ColumnPicker, PropertyCells, PropertyHeaders, usePropertyColumns } from '../properties/PropertyColumns';
import { useScoringSettings } from '../../api/scoring';
import { ScoreBadge, useScoringInUse } from '../scoring/scoringShared';

const PAGE_SIZE = 50;
const mono = { fontFamily: FONTS.mono, fontSize: 13, fontVariantNumeric: 'tabular-nums' } as const;

export function PeopleList({
  filter,
  filtered,
}: {
  /** The records page's filters, as the leads search takes them. */
  filter: LeadFilters;
  /** Whether any filter or segment is narrowing the rows, for the note. */
  filtered: boolean;
}) {
  const v = useVocab();
  const perms = useCrmPermissions();
  const cols = usePropertyColumns('person', perms.recordProperties);
  const columns = perms.recordProperties ? cols.columns : [];
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q.trim(), 250);
  const [sort, setSort] = useState<string>('last_seen');
  const [repeat, setRepeat] = useState(false);
  const [owner, setOwner] = useState('');
  // A person's score: best (or newest) lead score plus person rule points,
  // faded by decay. Sorted and banded on the server.
  const scoring = useScoringInUse();
  const bands = useScoringSettings(scoring).data?.bands ?? [];
  const [band, setBand] = useState('');
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<string[]>([]);
  const [assignAll, setAssignAll] = useState(false);
  // A new segment or filter starts from the first page, with nothing ticked.
  const filterKey = JSON.stringify(filter);
  useEffect(() => { setPage(1); setPicked([]); }, [filterKey]);
  const list = usePeopleSearch(
    {
      q: debounced, sort, page, page_size: PAGE_SIZE,
      ...(repeat ? { min_submissions: 2 } : {}),
      ...(owner && perms.owners ? { owner_user_id: owner } : {}),
      ...(band && scoring ? { score_band: band } : {}),
      filter: { ...filter, page: undefined, page_size: undefined },
    },
    perms.seesPeople,
  );
  const ticks = perms.assignRecords;
  // The inbox is part of linking, which goes with the CRM area.
  const settings = useIdentitySettings(perms.peopleLinking).data;
  const open = settings?.layer.open_suggestions ?? 0;

  const people = list.data?.people ?? [];
  const total = list.data?.pagination.total ?? 0;

  return (
    <>
      <Group gap="sm" align="center" wrap="wrap" data-testid="people-toolbar">
        <TextInput
          aria-label="Search"
          placeholder="Name, email or phone"
          leftSection={<IconSearch size={14} />}
          value={q}
          onChange={(e) => { setQ(e.currentTarget.value); setPage(1); }}
          w={240}
        />
        <Select
          aria-label="Sort"
          value={sort}
          onChange={(s) => { setSort(s ?? 'last_seen'); setPage(1); }}
          allowDeselect={false}
          data={[
            { value: 'last_seen', label: 'Last seen' },
            { value: 'first_seen', label: 'First seen' },
            { value: 'submissions', label: `Most ${v.many('lead')}` },
            { value: 'value', label: 'Highest value' },
            ...(scoring
              ? [{ value: 'score', label: 'Highest score' }, { value: 'score_asc', label: 'Lowest score' }]
              : []),
          ]}
          w={170}
        />
        {scoring && (
          <Select
            aria-label="Score band"
            placeholder="Any score"
            clearable
            value={band || null}
            onChange={(b) => { setBand(b ?? ''); setPage(1); }}
            data={bands.map((b) => ({ value: b.key, label: b.label }))}
            w={140}
            data-testid="people-band"
          />
        )}
        <Chip size="sm" variant="outline" checked={repeat} onChange={(c) => { setRepeat(c); setPage(1); }}>
          {`More than one ${v.one('lead')}`}
        </Chip>
        {perms.owners && (
          <OwnerFilterSelect value={owner} onChange={(o) => { setOwner(o); setPage(1); setPicked([]); }} />
        )}
        {ticks && (
          <Button variant="default" leftSection={<IconArrowsShuffle size={16} />} onClick={() => setAssignAll(true)}>
            Assign all matching
          </Button>
        )}
        {perms.peopleLinking && (
          <Button
            component={Link}
            to="/people/suggestions"
            variant="default"
            ml="auto"
            leftSection={<IconInbox size={16} />}
            rightSection={open > 0 ? <Badge size="sm" circle={open < 10} color="chartAmber">{formatNumber(open)}</Badge> : undefined}
          >
            Suggestions
          </Button>
        )}
      </Group>

      {list.data?.source === 'email' && (
        <Alert variant="light" color="gray" data-testid="people-email-note" py={8}>
          Grouped by email until stored people are switched on.
        </Alert>
      )}

      {list.error && <Alert color="red" variant="light">{list.error.message}</Alert>}

      <BulkAssignBar kind="people" ids={picked} onClear={() => setPicked([])} onDone={() => setPicked([])} />
      <AssignPeopleModal
        opened={assignAll}
        onClose={() => setAssignAll(false)}
        filter={{
          ...filter,
          ...(debounced ? { search: debounced } : {}),
          ...(owner ? { owner } : {}),
          ...(repeat ? { repeats_min: 2 } : {}),
        }}
      />

      <Card p={0}>
        <Group
          justify="space-between"
          px="md"
          py="xs"
          wrap="wrap"
          gap="xs"
          style={{ borderBottom: `1px solid ${TOKENS.border}` }}
        >
          <Group gap="md" align="flex-start">
            <div>
              <Group gap={6} align="baseline">
                <Text fw={700} data-testid="people-total">{formatNumber(total)}</Text>
                <Text size="xs" c="dimmed">{v.word(total, 'person')}</Text>
              </Group>
              <SpeedChip stats={list.data?.query_stats} />
            </div>
          </Group>
          {/* Every filter carries over, so this only says how they apply. */}
          {filtered && (
            <Text size="xs" c="dimmed" maw={460} data-testid="people-filter-note">
              {`Each ${v.one('person')} with at least one ${v.one('lead')} matching the filters. Their counts and value cover all their ${v.many('lead')}.`}
            </Text>
          )}
          <Group gap="xs">
            {list.isFetching && !list.isPending && <Loader size="xs" />}
            {perms.recordProperties && (
              <ColumnPicker defs={cols.defs} chosen={cols.chosen} onChange={cols.setChosen} />
            )}
          </Group>
        </Group>
        {list.isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={920}>
            <Table verticalSpacing={8} highlightOnHover data-testid="people-table">
              <Table.Thead>
                <Table.Tr>
                  {ticks && (
                    <Table.Th w={36}>
                      <Checkbox
                        aria-label="Select every row on this page"
                        checked={people.length > 0 && people.every((p) => picked.includes(p.id))}
                        indeterminate={picked.length > 0 && !people.every((p) => picked.includes(p.id))}
                        onChange={(e) => setPicked(e.currentTarget.checked ? people.map((p) => p.id) : [])}
                      />
                    </Table.Th>
                  )}
                  <Table.Th>Name</Table.Th>
                  {scoring && <Table.Th w={70}>Score</Table.Th>}
                  {perms.owners && <Table.Th>{v.One('owner')}</Table.Th>}
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Phone</Table.Th>
                  <Table.Th ta="right">{v.Many('lead')}</Table.Th>
                  <Table.Th>First seen</Table.Th>
                  <Table.Th>Last seen</Table.Th>
                  <Table.Th ta="right">Value</Table.Th>
                  <Table.Th>{v.Many('campaign')}</Table.Th>
                  <PropertyHeaders columns={columns} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {people.length === 0 && (
                  <Table.Tr><Table.Td colSpan={10 + columns.length + (scoring ? 1 : 0)}>
                    <Text ta="center" c="dimmed" py="lg">
                      {debounced || repeat || filtered ? `No ${v.many('person')} match.` : `No ${v.many('person')} yet.`}
                    </Text>
                  </Table.Td></Table.Tr>
                )}
                {people.map((p) => (
                  <Table.Tr key={p.id} onClick={() => navigate(`/people/${encodeURIComponent(p.id)}`)} style={{ cursor: 'pointer' }}>
                    {ticks && (
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          aria-label={`Select ${personName(p)}`}
                          checked={picked.includes(p.id)}
                          onChange={(e) => {
                            const on = e.currentTarget.checked;
                            setPicked((all) => (on ? [...all, p.id] : all.filter((x) => x !== p.id)));
                          }}
                        />
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Anchor
                        size="sm"
                        fw={500}
                        component={Link}
                        to={`/people/${encodeURIComponent(p.id)}`}
                        onClick={(e) => e.stopPropagation()}
                        c="inherit"
                      >
                        {personName(p)}
                      </Anchor>
                    </Table.Td>
                    {scoring && (
                      <Table.Td><ScoreBadge score={p.score} band={p.score_band} size="xs" /></Table.Td>
                    )}
                    {perms.owners && (
                      <Table.Td>
                        {p.owner_name
                          ? <Text size="sm">{p.owner_name}</Text>
                          : <Text size="sm" c="dimmed">Nobody</Text>}
                      </Table.Td>
                    )}
                    <Table.Td><Text size="sm" truncate="end" maw={240}>{p.email}</Text></Table.Td>
                    <Table.Td><Text size="sm" style={{ fontFamily: FONTS.mono, fontSize: 12.5 }}>{p.phone}</Text></Table.Td>
                    <Table.Td ta="right"><span style={mono}>{formatNumber(p.submissions)}</span></Table.Td>
                    <Table.Td><Text size="sm">{formatDay(p.first_seen)}</Text></Table.Td>
                    <Table.Td>
                      <Tooltip label={formatDay(p.last_seen)} withArrow openDelay={300}>
                        <Text size="sm" span>{relativeTime(p.last_seen)}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td ta="right"><span style={mono}>{formatGBP(p.total_value)}</span></Table.Td>
                    <Table.Td><StreamChips streams={p.streams} max={2} /></Table.Td>
                    <PropertyCells columns={columns} values={p.properties} />
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {(list.data?.pagination.total_pages ?? 0) > 1 && (
          <Group justify="flex-end" p="sm">
            <Pagination size="sm" total={list.data?.pagination.total_pages ?? 1} value={page} onChange={setPage} />
          </Group>
        )}
      </Card>
    </>
  );
}
